import {
  type GameState,
  type LineupEntry,
  type LineupPositions,
  postDataTagMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { Button, toast } from "@win-reality/react-ui";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getErrorDetail } from "@/utils/errorUtils";

import { incrementPitchCount } from "../../../../store/slices/game";
import { setTaggingFlowStep } from "../../../../store/slices/hotkeys";
import {
  resetAllTags,
  setTaggingState,
  setTags,
} from "../../../../store/slices/session";
import {
  addEventToQueue,
  resetQueueLength,
  setFocusedEventPayload,
  setIsTaggingInterruptedEvent,
  setIsViewingPreviousEvent,
  setStaleEvents,
  updateEmptyEvent,
  updateQueueEvent,
} from "../../../../store/slices/socket";
import {
  selectEventQueue,
  selectFocusedEvent,
  selectMetricsAreAvailable,
  selectRecentEvent,
} from "../../../../store/slices/socket/selectors";
import type { RootState } from "../../../../store/store";
import type { IGameState, IQueueEvent } from "../../../../store/types";
import { mapIPlayerToLineupEntry } from "../../../../utils/api-helpers";
import { emptyEventMetricSpawner } from "../../../../utils/emptyEventMetricSpawner";
import { useUpdateGameState } from "../../../../utils/useUpdateGameState";

const getTagPayload = (
  currentHitter: IPlayer,
  currentPitcher: IPlayer,
  currentCatcher: IPlayer,
  lineUp: (IPlayer & { position: number | LineupPositions })[],
): {
  currentHitter: LineupEntry;
  currentPitcher: LineupEntry;
  currentCatcher: LineupEntry;
} => {
  const hitterPosition = lineUp.find(
    (player) => player.uuid === currentHitter.uuid,
  )?.position;
  return {
    currentHitter: mapIPlayerToLineupEntry(currentHitter, hitterPosition!),
    currentPitcher: mapIPlayerToLineupEntry(currentPitcher, "pitcher"),
    currentCatcher: mapIPlayerToLineupEntry(currentCatcher, "catcher"),
  };
};

/**
 * SubmissionButton - Handles tag submission and updates
 *
 * Features:
 * - Saves tags for current event via API
 * - Updates previously tagged events
 * - Handles cases where no metrics are available
 * - Updates game state after successful submission
 * - Supports Enter key for submission
 * - Shows loading state during API call
 * - Different button text based on viewing state
 */
export const SubmissionButton = () => {
  const [mostRecentTaggedEventTime, setMostRecentTaggedEventTime] = useState<
    number | null
  >(null);

  const dispatch = useDispatch();
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const updateGameState = useUpdateGameState();
  const recentEvent = useSelector(selectRecentEvent);
  const focusedEvent = useSelector(selectFocusedEvent);
  const eventQueue = useSelector(selectEventQueue);
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const rosters = useSelector((state: RootState) => state.game.rosters);
  const isTagging = useSelector((state: RootState) => state.session.isTagging);
  const areMetricsAvailable = useSelector(selectMetricsAreAvailable);
  const isTaggingInterruptedEvent = useSelector(
    (state: RootState) => state.socket.isTaggingInterruptedEvent,
  );
  const pitchingTeam = gameState.inning.half === "top" ? "home" : "away";
  const currentPitcher =
    gameState.inning.half === "top"
      ? rosters.home.currentPitcher
      : rosters.away.currentPitcher;
  const currentCatcher =
    gameState.inning.half === "top"
      ? rosters.home.currentCatcher
      : rosters.away.currentCatcher;
  const currentHitter =
    gameState.inning.half === "top"
      ? rosters.away.currentHitter
      : rosters.home.currentHitter;

  const { id: machineId } = useParams() as { id: string };

  // Filter lineup to batting positions only (positions 1-9, which are numbers)
  const offenseLineup =
    pitchingTeam === "home"
      ? rosters.away.currentLineup
      : rosters.home.currentLineup;
  const battingOnlyLineup = (
    offenseLineup as (IPlayer & { position: number | LineupPositions })[]
  ).filter((p) => typeof p.position === "number");

  const { mutate: saveDataTag, isPending: isLoadingSaveTags } = useMutation({
    ...postDataTagMutation(),
    onError: (error: Error) => {
      const errorMessage = getErrorDetail(error);
      toast(`Error while saving tags: ${errorMessage}`, {
        variant: "error",
      });
    },
  });

  const handlePitchConfirm = () => {
    // Capture the gameState at submission time (before backend response overwrites it)
    const submittedGameState = gameState;

    // This block runs if the user wants to save tags but there's no event present.
    // It is useful for when the user wants to advance gamestate but cameras missed a pitch.
    if (Object.keys(recentEvent).length === 0) {
      saveDataTag(
        {
          body: {
            // @ts-expect-error The API spec is wrong. There are some required props which are actually optional.
            payload: {
              event_time: Date.now() / 1000,
              machine_uuid: machineId,
              tag_data: {
                metadata: {
                  ...tagData,
                },
                gameState: {
                  ...(submittedGameState as GameState),
                  ...getTagPayload(
                    currentHitter,
                    currentPitcher,
                    currentCatcher,
                    battingOnlyLineup as (IPlayer & {
                      position: number | LineupPositions;
                    })[],
                  ),
                },
              },
            },
          },
        },
        {
          onSuccess: (data) => {
            const { event_uuid, gameState } = data;
            if (!gameState) {
              console.error("The backend didn't return a gameState");
              return;
            }
            dispatch(setIsTaggingInterruptedEvent(false));
            dispatch(setIsViewingPreviousEvent(false));
            dispatch(setFocusedEventPayload({}));
            dispatch(resetQueueLength());
            dispatch(resetAllTags({ key: "prePitchStance" }));
            dispatch(setTaggingFlowStep(1));
            dispatch(incrementPitchCount({ team: pitchingTeam }));
            // Add non metric event here:
            const emptyMetricEvent = emptyEventMetricSpawner();
            // Add event to queue
            dispatch(addEventToQueue(emptyMetricEvent as IQueueEvent));
            // Update event in queue
            dispatch(
              updateQueueEvent({
                ...(emptyMetricEvent as IQueueEvent),
                tagData,
                focusedPlayers: {
                  focusedPitcher: currentPitcher,
                  focusedCatcher: currentCatcher,
                  focusedHitter: currentHitter,
                },
                gameState: gameState as IGameState,
              }),
            );
            dispatch(
              updateEmptyEvent({
                currentUuid: emptyMetricEvent.event_uuid,
                createdUuid: event_uuid!,
              }),
            );
            updateGameState(gameState, data.game);

            // Update most recent tagged event timestamp
            const newEventTime =
              emptyMetricEvent.event_time || Date.now() / 1000;
            if (
              !mostRecentTaggedEventTime ||
              newEventTime > mostRecentTaggedEventTime
            ) {
              setMostRecentTaggedEventTime(newEventTime);
            }
          },
        },
      );

      return;
    }

    saveDataTag(
      {
        body: {
          payload: {
            event_uuid: recentEvent.event_uuid,
            event_time: recentEvent.event_time!,
            machine_uuid: machineId,
            tag_data: {
              metadata: {
                ...tagData,
              },
              gameState: {
                ...(submittedGameState as GameState),
                ...getTagPayload(
                  currentHitter,
                  currentPitcher,
                  currentCatcher,
                  battingOnlyLineup as (IPlayer & {
                    position: number | LineupPositions;
                  })[],
                ),
              },
            },
          },
        },
      },
      {
        onSuccess: (data) => {
          const { gameState, game } = data;
          if (!gameState || !game) {
            console.error("The backend didn't return a gameState");
            return;
          }
          dispatch(
            updateQueueEvent({
              ...recentEvent,
              tagData,
              focusedPlayers: {
                focusedPitcher: currentPitcher,
                focusedCatcher: currentCatcher,
                focusedHitter: currentHitter,
              },
              gameState: submittedGameState as IGameState,
            }),
          );

          dispatch(setIsTaggingInterruptedEvent(false));
          dispatch(setIsViewingPreviousEvent(false));
          dispatch(setFocusedEventPayload({}));
          dispatch(resetQueueLength());
          dispatch(setTaggingState(false));
          dispatch(setStaleEvents(recentEvent.contributing_events || []));
          dispatch(resetAllTags({ key: "prePitchStance" }));
          dispatch(setTaggingFlowStep(1));
          dispatch(incrementPitchCount({ team: pitchingTeam }));
          updateGameState(gameState, game);

          // Update most recent tagged event timestamp
          const newEventTime = recentEvent.event_time || Date.now() / 1000;
          if (
            !mostRecentTaggedEventTime ||
            newEventTime > mostRecentTaggedEventTime
          ) {
            setMostRecentTaggedEventTime(newEventTime);
          }
        },
      },
    );
  };

  // Update a focusedEvent if isViewingPreviousEvent is true
  const handleUpdateEvent = () => {
    if (Object.keys(focusedEvent).length === 0) {
      toast("Please select an event to update", {
        variant: "error",
      });
      return;
    }

    saveDataTag(
      {
        body: {
          payload: {
            event_uuid: focusedEvent.event_uuid,
            event_time: focusedEvent.event_time!,
            machine_uuid: machineId,
            tag_data: {
              metadata: {
                ...tagData,
              },
              gameState: {
                ...(focusedEvent.gameState as GameState),
                ...getTagPayload(
                  currentHitter,
                  currentPitcher,
                  currentCatcher,
                  battingOnlyLineup as (IPlayer & {
                    position: number | LineupPositions;
                  })[],
                ),
              },
            },
          },
        },
      },
      {
        onSuccess: (data) => {
          const { gameState, game } = data;
          if (!gameState || !game) {
            console.error("The backend didn't return a gameState");
            return;
          }
          if (isTaggingInterruptedEvent) {
            dispatch(
              updateQueueEvent({
                ...focusedEvent,
                tagData,
                focusedPlayers: {
                  focusedPitcher: focusedEvent?.focusedPlayers?.focusedPitcher,
                  focusedCatcher: focusedEvent?.focusedPlayers?.focusedCatcher,
                  focusedHitter: focusedEvent?.focusedPlayers?.focusedHitter,
                },
                gameState: gameState as IGameState,
              }),
            );
          } else {
            dispatch(
              updateQueueEvent({
                ...focusedEvent,
                tagData,
                focusedPlayers: {
                  focusedPitcher: focusedEvent?.focusedPlayers?.focusedPitcher,
                  focusedCatcher: focusedEvent?.focusedPlayers?.focusedCatcher,
                  focusedHitter: focusedEvent?.focusedPlayers?.focusedHitter,
                },
              }),
            );
          }

          dispatch(setIsTaggingInterruptedEvent(false));
          dispatch(setTaggingState(false));
          dispatch(resetQueueLength());
          dispatch(resetAllTags({ key: "prePitchStance" }));

          // Find next untagged event (queue is newest-first, so iterate backward through indices)
          const focusedIndex = eventQueue.findIndex(
            (e) => e.event_uuid === focusedEvent.event_uuid,
          );

          let nextUntaggedEvent = null;

          // Only search if focused event is in queue
          if (focusedIndex !== -1) {
            for (let i = focusedIndex - 1; i >= 0; i--) {
              const event = eventQueue[i];
              if (!event.tagData || Object.keys(event.tagData).length === 0) {
                nextUntaggedEvent = event;
                break;
              }
            }
          }

          // If no untagged event found, fall back to current/most recent event
          if (nextUntaggedEvent) {
            const isCurrentEvent =
              nextUntaggedEvent.event_uuid === recentEvent.event_uuid;

            dispatch(setIsViewingPreviousEvent(!isCurrentEvent));
            dispatch(
              setFocusedEventPayload({
                event_uuid: nextUntaggedEvent.event_uuid,
                gameState: gameState,
                focusedPlayers: {
                  focusedPitcher: currentPitcher,
                  focusedHitter: currentHitter,
                  focusedCatcher: currentCatcher,
                },
              }),
            );
            dispatch(setTaggingFlowStep(0));
            dispatch(setTags({}));
          } else {
            // No untagged event found, focus on current event
            dispatch(setIsViewingPreviousEvent(false));
            dispatch(setFocusedEventPayload({}));
            dispatch(setTaggingFlowStep(1));
          }

          const isCurrentEvent =
            focusedEvent.event_uuid === recentEvent.event_uuid;
          const isMostRecentTaggedEvent =
            !mostRecentTaggedEventTime ||
            (focusedEvent.event_time || 0) >= mostRecentTaggedEventTime;

          if (isCurrentEvent || isMostRecentTaggedEvent) {
            updateGameState(gameState, game);
          }
        },
      },
    );
  };

  const handleEnter = () => {
    // When viewing a previous event, allow update regardless of current event metrics
    // Only check metrics availability when tagging the current event
    if (!isViewingPreviousEvent && !isTagging && !areMetricsAvailable) {
      toast("Waiting for pitch data...", {
        variant: "info",
        position: "top-center",
      });
      return;
    }

    if (isViewingPreviousEvent) {
      handleUpdateEvent();
    } else {
      handlePitchConfirm();
    }
  };

  // Custom handling for enter key submission to address long execution times
  // when using Event: keydown. Not sure why yet, but this submissions with ENTER
  // were taking 10x longer than clicking the button.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const button = document.getElementById("submit-button");
        if (button) {
          button.click();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Button
      id="submit-button"
      type="submit"
      variant="secondary-flat-light"
      onClick={handleEnter}
      isLoading={isLoadingSaveTags}
      isDisabled={isLoadingSaveTags}
    >
      {isViewingPreviousEvent ? "Update Tags" : "Save Tags"}
    </Button>
  );
};
